import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { reviewSessionService } from '../../services/reviewSession.service';
import { reviewSessionActions } from '../../redux/actions/reviewSession.actions';
import { classActions, userActions } from '../../redux/actions';
import styled, { keyframes, css } from 'styled-components';
import apiClient from '../../helpers/apiClient';

// ─── Animations ──────────────────────────────────────────────────────────────

const fadeIn = keyframes`from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}`;
const slideUp = keyframes`from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}`;
const spin = keyframes`to{transform:rotate(360deg)}`;

// ─── Styled Components ────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  padding: 24px;
  font-family: 'Segoe UI', sans-serif;
  animation: ${fadeIn} 0.4s ease;
`;

const TopBar = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 16px; flex-wrap: wrap; gap: 12px;
`;

const PageTitle = styled.h2`
  font-size: 22px; font-weight: 700; color: #1a1a2e; margin: 0;
`;

const FilterRow = styled.div`
  display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 18px;
`;

const FilterTag = styled.div<{ isActive: boolean }>`
  padding: 6px 14px;
  border-radius: 20px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  user-select: none;
  transition: all 0.2s ease;
  background: ${p => p.isActive ? 'linear-gradient(135deg, #007bff, #0056b3)' : 'linear-gradient(135deg, #f8f9fa, #e9ecef)'};
  color: ${p => p.isActive ? '#fff' : '#495057'};
  box-shadow: ${p => p.isActive ? '0 4px 8px rgba(0,123,255,0.3)' : '0 2px 4px rgba(0,0,0,0.08)'};
  transform: ${p => p.isActive ? 'translateY(-2px)' : 'none'};
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${p => p.isActive ? '0 6px 12px rgba(0,123,255,0.4)' : '0 4px 8px rgba(0,0,0,0.12)'};
  }
`;

const StyledTable = styled.table`
  width: 100%; border-collapse: collapse; background: #fff;
  border-radius: 12px; overflow: hidden; box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  th {
    background: #f0f4ff; padding: 11px 14px; text-align: left;
    font-size: 13px; font-weight: 600; color: #555; border-bottom: 1px solid #e5e7eb;
    white-space: nowrap;
  }
  td {
    padding: 11px 14px; font-size: 13px; color: #333;
    border-bottom: 1px solid #f0f0f0; vertical-align: middle;
  }
  tr:last-child td { border-bottom: none; }
  tr:hover td { background: #fafbff; }
`;

const Btn = styled.button<{ variant?: 'primary' | 'danger' | 'success' | 'secondary' | 'export' }>`
  padding: 5px 12px; border-radius: 8px; border: none; font-size: 12px;
  font-weight: 600; cursor: pointer; transition: all 0.2s; white-space: nowrap;
  ${({ variant }) => {
    switch (variant) {
      case 'primary':  return 'background:#667eea;color:#fff;';
      case 'danger':   return 'background:#ef4444;color:#fff;';
      case 'success':  return 'background:#22c55e;color:#fff;';
      case 'export':   return 'background:linear-gradient(135deg,#1d4ed8,#3b82f6);color:#fff;padding:8px 18px;font-size:13px;border-radius:10px;';
      default:         return 'background:#e5e7eb;color:#333;';
    }
  }}
  &:hover { opacity: 0.85; transform: translateY(-1px); }
  & + & { margin-left: 6px; }
`;

const ClassBadge = styled.span`
  background: #ede9fe; color: #7c3aed;
  padding: 2px 10px; border-radius: 10px;
  font-size: 11px; font-weight: 700;
`;

const EmptyMsg = styled.div`
  text-align: center; padding: 48px; color: #999;
  font-size: 14px; background: #fff; border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
`;

// ─── Word List Export Modal ───────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.45);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999; padding: 20px;
`;

const ModalBox = styled.div`
  background: #fff; border-radius: 20px;
  padding: 32px; width: 100%; max-width: 520px;
  box-shadow: 0 24px 60px rgba(0,0,0,0.2);
  animation: ${slideUp} 0.3s ease;
`;

const ModalTitle = styled.h3`
  margin: 0 0 6px; font-size: 20px; font-weight: 800;
  background: linear-gradient(135deg,#1d4ed8,#3b82f6);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
`;

const ModalSubtitle = styled.p`
  margin: 0 0 24px; font-size: 13px; color: #6b7280;
`;

const FormGroup = styled.div`
  margin-bottom: 18px;
  label {
    display: block; font-size: 12px; font-weight: 700;
    color: #374151; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px;
  }
`;

const StyledInput = styled.input`
  width: 100%; padding: 10px 14px; border-radius: 10px;
  border: 1.5px solid #e5e7eb; font-size: 13px;
  outline: none; transition: border-color 0.2s; box-sizing: border-box;
  &:focus { border-color: #3b82f6; }
`;

const StyledSelect = styled.select`
  width: 100%; padding: 10px 14px; border-radius: 10px;
  border: 1.5px solid #e5e7eb; font-size: 13px;
  outline: none; transition: border-color 0.2s; box-sizing: border-box;
  background: #fff; cursor: pointer;
  &:focus { border-color: #a78bfa; }
`;

const CheckboxGroup = styled.div`
  display: flex; gap: 12px; flex-wrap: wrap;
`;

const CheckLabel = styled.label<{ checked: boolean }>`
  display: flex; align-items: center; gap: 8px;
  padding: 10px 16px; border-radius: 12px; cursor: pointer;
  font-size: 13px; font-weight: 600; transition: all 0.2s; user-select: none;
  border: 2px solid ${p => p.checked ? '#1d4ed8' : '#e5e7eb'};
  background: ${p => p.checked ? '#eff6ff' : '#fff'};
  color: ${p => p.checked ? '#1d4ed8' : '#6b7280'};
  &:hover { border-color: #3b82f6; background: #eff6ff; }
  input { display: none; }
`;

const ModalFooter = styled.div`
  display: flex; gap: 10px; justify-content: flex-end; margin-top: 28px;
`;

const ResultBox = styled.div`
  margin-top: 20px; padding: 16px; border-radius: 12px;
  background: linear-gradient(135deg,#eff6ff,#dbeafe);
  border: 1.5px solid #93c5fd;
`;

const Spinner = styled.div`
  width: 18px; height: 18px; border: 2px solid #93c5fd;
  border-top-color: #1d4ed8; border-radius: 50%;
  animation: ${spin} 0.7s linear infinite; display: inline-block;
`;

const WordRow = styled.div`
  padding: 8px 12px; border-radius: 8px; margin-bottom: 6px;
  background: #fff; border: 1px solid #bfdbfe; font-size: 13px;
  &:last-child { margin-bottom: 0; }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const ReviewDashboard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [sessions, setSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState<number | null>(null);
  const [selectedClass, setSelectedClass] = useState<number | null>(null);

  // Word-list export modal state
  const [showExport, setShowExport] = useState(false);
  const [exportDateFrom, setExportDateFrom] = useState('');
  const [exportDateTo, setExportDateTo] = useState('');
  const [exportClassId, setExportClassId] = useState('');
  const [exportTypes, setExportTypes] = useState<'vocab' | 'sentence' | 'both'>('both');
  const [exportLoading, setExportLoading] = useState(false);
  const [exportResult, setExportResult] = useState<any[] | null>(null);
  const [exportError, setExportError] = useState('');

  // Current user
  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
  const isAdmin = userInfo.role === 0;

  const classes  = useAppSelector((state: any) => state.classes?.classesList || []);
  const teachers = useAppSelector((state: any) => state.users?.items || []);

  const teacherMap = new Map<number, string>();
  teachers.forEach((t: any) => { teacherMap.set(t.id, t.fullName || t.username || '—'); });

  const myClasses = isAdmin
    ? [...classes].sort((a: any, b: any) => a.name.localeCompare(b.name))
    : [...classes]
        .filter((cls: any) => cls.teacherId === userInfo.id)
        .sort((a: any, b: any) => a.name.localeCompare(b.name));

  const myClassIds = new Set(myClasses.map((c: any) => c.id));

  const load = async () => {
    setLoading(true);
    try {
      const res = await reviewSessionService.getAll();
      setSessions(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    Promise.all([
      load(),
      dispatch(classActions.getAll()),
      dispatch(userActions.getAllTeachers()),
    ]);
  }, []);

  const handleCopyLink = (sessionId: number) => {
    const link = `${window.location.origin}/review/${sessionId}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopied(sessionId);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Xoá bài ôn tập này?')) return;
    await dispatch(reviewSessionActions.deleteSession(id));
    setSessions(prev => prev.filter(s => s.id !== id));
  };

  const formatDateTime = (d: string) => {
    if (!d) return '—';
    const date = new Date(d);
    const dd = date.getDate().toString().padStart(2, '0');
    const mm = (date.getMonth() + 1).toString().padStart(2, '0');
    const yyyy = date.getFullYear();
    const hh = date.getHours().toString().padStart(2, '0');
    const min = date.getMinutes().toString().padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  };

  const visibleSessions = (isAdmin
    ? sessions
    : sessions.filter(s => s.class_id == null || myClassIds.has(s.class_id))
  ).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const filtered = selectedClass === null
    ? visibleSessions
    : visibleSessions.filter(s => s.class_id === selectedClass);

  // ── Word list export ──────────────────────────────────────────────────────

  const handleExportFetch = async () => {
    setExportLoading(true);
    setExportError('');
    setExportResult(null);
    try {
      const params = new URLSearchParams();
      if (exportDateFrom) params.set('date_from', exportDateFrom);
      if (exportDateTo)   params.set('date_to',   exportDateTo);
      if (exportClassId)  params.set('class_id',  exportClassId);
      params.set('types', exportTypes);
      const res = await apiClient.get(`/api/review-sessions/word-list/export?${params}`);
      const data: any[] = res.data?.data || [];
      setExportResult(data);
      if (data.length === 0) setExportError('Không tìm thấy từ nào trong khoảng thời gian đã chọn.');
    } catch (e: any) {
      setExportError(e?.response?.data?.message || 'Lỗi tải danh sách từ.');
    } finally {
      setExportLoading(false);
    }
  };

  const handleDownloadDocx = async () => {
    if (!exportResult || exportResult.length === 0) return;
    try {
      const params = new URLSearchParams();
      if (exportDateFrom) params.set('date_from', exportDateFrom);
      if (exportDateTo)   params.set('date_to',   exportDateTo);
      if (exportClassId)  params.set('class_id',  exportClassId);
      params.set('types', exportTypes);

      // Use fetch directly to get a binary blob; apiClient may not handle blob response type
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token') || '';
      const res = await fetch(
        `${process.env.REACT_APP_API_URL || ''}/api/review-sessions/word-list/export-docx?${params}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error('Lỗi tải file');
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `word-list_${exportDateFrom || 'all'}_${exportDateTo || 'all'}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      alert('Lỗi tải file Word: ' + (e?.message || ''));
    }
  };

  const handleCloseExport = () => {
    setShowExport(false);
    setExportResult(null);
    setExportError('');
  };

  return (
    <PageWrapper>
      <TopBar>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <PageTitle>📖 Quản lý Bài Ôn Tập</PageTitle>
          <Btn variant="export" onClick={() => setShowExport(true)}>
            📥 Tải danh sách từ
          </Btn>
        </div>
        <span style={{ fontSize: 13, color: '#888' }}>
          {filtered.length} / {sessions.length} bài ôn tập
        </span>
      </TopBar>

      {/* ─── Class filter tags ─── */}
      <FilterRow>
        <FilterTag isActive={selectedClass === null} onClick={() => setSelectedClass(null)}>
          Tất cả lớp
        </FilterTag>
        {myClasses.map((cls: any) => (
          <FilterTag
            key={cls.id}
            isActive={selectedClass === cls.id}
            onClick={() => setSelectedClass(selectedClass === cls.id ? null : cls.id)}
          >
            {cls.name}
          </FilterTag>
        ))}
      </FilterRow>

      {loading ? (
        <EmptyMsg>⏳ Đang tải...</EmptyMsg>
      ) : filtered.length === 0 ? (
        <EmptyMsg>
          {sessions.length === 0
            ? <>Chưa có bài ôn tập nào. Hãy vào <strong>Nhật Kí Dạy Học</strong> và tạo bài ôn tập từ nhật kí.</>
            : 'Không có bài ôn tập nào cho lớp đã chọn.'
          }
        </EmptyMsg>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <StyledTable>
            <thead>
              <tr>
                <th>#</th>
                <th>ID</th>
                <th>Tên bài ôn tập</th>
                <th>Lớp</th>
                <th>Người tạo</th>
                <th>Ngày/giờ tạo</th>
                <th>Ngày học</th>
                <th>Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s, idx) => {
                const creatorName = s.creator?.userName
                  || (s.created_by ? teacherMap.get(s.created_by) : null)
                  || '—';
                const classObj = s.class || classes.find((c: any) => c.id === s.class_id);
                const className = classObj?.name;
                return (
                  <tr key={s.id}>
                    <td style={{ color: '#999', width: 36 }}>{idx + 1}</td>
                    <td style={{ width: 56 }}>
                      <span style={{
                        fontFamily: 'monospace', fontSize: 11, fontWeight: 700,
                        background: '#f1f5f9', color: '#475569',
                        border: '1px solid #e2e8f0', borderRadius: 6,
                        padding: '2px 7px', whiteSpace: 'nowrap',
                      }}>
                        #{s.id}
                      </span>
                    </td>
                    <td style={{ fontWeight: 600 }}>{s.name}</td>
                    <td>
                      {className
                        ? <ClassBadge>🏫 {className}</ClassBadge>
                        : <span style={{ color: '#bbb' }}>—</span>
                      }
                    </td>
                    <td style={{ color: '#475569' }}>{creatorName}</td>
                    <td style={{ color: '#64748b', whiteSpace: 'nowrap' }}>
                      {formatDateTime(s.created_at)}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {s.lesson_date
                        ? new Date(s.lesson_date).toLocaleDateString('vi-VN')
                        : '—'}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <Btn variant="primary" onClick={() => navigate(`/review/${s.id}`)}>
                        👁️ Xem
                      </Btn>
                      <Btn variant="success" onClick={() => handleCopyLink(s.id)}>
                        {copied === s.id ? '✅ Đã copy!' : '🔗 Copy Link'}
                      </Btn>
                      <Btn variant="danger" onClick={() => handleDelete(s.id)}>
                        🗑️ Xoá
                      </Btn>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </StyledTable>
        </div>
      )}

      {/* ─── Export Word List Modal ─── */}
      {showExport && (
        <Overlay onClick={e => { if (e.target === e.currentTarget) handleCloseExport(); }}>
          <ModalBox>
            <ModalTitle>📥 Tải danh sách từ vựng</ModalTitle>
            <ModalSubtitle>Lấy tất cả từ vựng / mẫu câu từ bài ôn tập theo khoảng thời gian</ModalSubtitle>

            {/* Date range */}
            <div style={{ display: 'flex', gap: 12 }}>
              <FormGroup style={{ flex: 1 }}>
                <label>Từ ngày</label>
                <StyledInput
                  type="date"
                  value={exportDateFrom}
                  onChange={e => setExportDateFrom(e.target.value)}
                />
              </FormGroup>
              <FormGroup style={{ flex: 1 }}>
                <label>Đến ngày</label>
                <StyledInput
                  type="date"
                  value={exportDateTo}
                  onChange={e => setExportDateTo(e.target.value)}
                />
              </FormGroup>
            </div>

            {/* Class filter */}
            <FormGroup>
              <label>Lớp học (tuỳ chọn)</label>
              <StyledSelect value={exportClassId} onChange={e => setExportClassId(e.target.value)}>
                <option value="">— Tất cả lớp —</option>
                {myClasses.map((cls: any) => (
                  <option key={cls.id} value={cls.id}>{cls.name}</option>
                ))}
              </StyledSelect>
            </FormGroup>

            {/* Type selector */}
            <FormGroup>
              <label>Loại nội dung</label>
              <CheckboxGroup>
                {(['both', 'vocab', 'sentence'] as const).map(t => (
                  <CheckLabel key={t} checked={exportTypes === t}>
                    <input
                      type="radio"
                      name="exportTypes"
                      value={t}
                      checked={exportTypes === t}
                      onChange={() => setExportTypes(t)}
                    />
                    {t === 'both' ? '📚 Cả hai' : t === 'vocab' ? '🔤 Từ vựng' : '💬 Mẫu câu'}
                  </CheckLabel>
                ))}
              </CheckboxGroup>
            </FormGroup>

            {/* Result preview */}
            {exportError && (
              <div style={{ padding: '12px 16px', borderRadius: 10, background: '#fef2f2', color: '#dc2626', fontSize: 13, marginBottom: 12 }}>
                ⚠️ {exportError}
              </div>
            )}

            {exportResult && exportResult.length > 0 && (
              <ResultBox>
                <div style={{ fontWeight: 700, color: '#1e40af', marginBottom: 10, fontSize: 13 }}>
                  ✅ Tìm thấy <strong>{exportResult.length}</strong> mục
                  ({exportResult.filter(w => w.type === 'vocab').length} từ vựng,&nbsp;
                  {exportResult.filter(w => w.type === 'sentence').length} mẫu câu)
                </div>
                {/* Preview first 5 */}
                <div style={{ maxHeight: 160, overflowY: 'auto' }}>
                  {exportResult.slice(0, 5).map((w, i) => (
                    <WordRow key={i}>
                      <span style={{ fontWeight: 700, color: '#1f2937' }}>{w.front}</span>
                      {w.phonetic && <span style={{ color: '#2563eb', marginLeft: 6 }}>/{w.phonetic}/</span>}
                      {w.part_of_speech && <span style={{ color: '#9ca3af', fontSize: 11, marginLeft: 6 }}>({w.part_of_speech})</span>}
                      <span style={{ color: '#6b7280', marginLeft: 8, fontSize: 12 }}>→ {w.back}</span>
                    </WordRow>
                  ))}
                  {exportResult.length > 5 && (
                    <div style={{ textAlign: 'center', color: '#2563eb', fontSize: 12, marginTop: 6 }}>
                      ... và {exportResult.length - 5} mục khác
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                  <button
                    onClick={handleDownloadDocx}
                    style={{
                      flex: 1, padding: '10px', borderRadius: 10, border: 'none',
                      background: 'linear-gradient(135deg,#1d4ed8,#3b82f6)', color: '#fff',
                      fontWeight: 700, fontSize: 13, cursor: 'pointer',
                    }}
                  >
                    📄 Tải file Word (.docx)
                  </button>
                </div>
              </ResultBox>
            )}

            <ModalFooter>
              <button
                onClick={handleCloseExport}
                style={{
                  padding: '10px 20px', borderRadius: 10, border: '1.5px solid #e5e7eb',
                  background: '#fff', color: '#374151', fontWeight: 600, fontSize: 13, cursor: 'pointer',
                }}
              >
                Đóng
              </button>
              <button
                onClick={handleExportFetch}
                disabled={exportLoading}
                style={{
                  padding: '10px 24px', borderRadius: 10, border: 'none',
                  background: exportLoading ? '#93c5fd' : 'linear-gradient(135deg,#1d4ed8,#3b82f6)',
                  color: '#fff', fontWeight: 700, fontSize: 13, cursor: exportLoading ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}
              >
                {exportLoading ? <><Spinner /> Đang tải...</> : '🔍 Xem danh sách'}
              </button>
            </ModalFooter>
          </ModalBox>
        </Overlay>
      )}
    </PageWrapper>
  );
};

export default ReviewDashboard;
